// src/services/fileStorageService.ts
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  type UploadTaskSnapshot,
  deleteObject
} from 'firebase/storage';
import { storage } from '../firebaseConfig';

export interface StoredAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
}

export class FileStorageService {
  // Upload a file to projects/{projectId}/items/{itemId}/{attachmentId}_{filename}
  static uploadFile(
    file: File,
    projectId: number | string,
    itemId: number | string,
    userId: string,
    attachmentId: string,
    onProgress?: (snapshot: UploadTaskSnapshot) => void
  ): Promise<StoredAttachment> {
    const storagePath = `projects/${projectId}/items/${itemId}/${attachmentId}_${file.name}`;
    const metadata = {
      contentType: file.type,
      customMetadata: {
        uploadedBy: userId,
        originalName: file.name,
        attachmentId: attachmentId,
        uploadedAt: ''
      }
    };

    return new Promise<StoredAttachment>((resolve, reject) => {
      const uploadTask = uploadBytesResumable(ref(storage, storagePath), file, metadata);

      uploadTask.on('state_changed',
        (snapshot) => onProgress?.(snapshot),
        (error) => reject(new Error(`Failed to upload file ${file.name}: ${error.message}`)),
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({
              id: attachmentId,
              name: file.name,
              type: file.type,
              size: file.size,
              url: downloadURL,
              storagePath: storagePath,
              uploadedAt: metadata.customMetadata.uploadedAt,
              uploadedBy: userId
            });
          } catch (error) {
            reject(new Error(`Failed to get download URL for ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`));
          }
        }
      );
    });
  }

  // Delete a file from Firebase Storage; a missing file is reported, not thrown
  static async deleteFile(storagePath: string): Promise<{ success: boolean; fileNotFound: boolean; error?: string }> {
    try {
      await deleteObject(ref(storage, storagePath));
      return { success: true, fileNotFound: false };
    } catch (error: any) {
      const isFileNotFound = error?.code === 'storage/object-not-found' ||
                            error?.message?.includes('object-not-found') ||
                            error?.message?.includes('No such object') ||
                            error?.message?.includes('not found');
      if (isFileNotFound) {
        return { success: false, fileNotFound: true, error: 'File not found in storage' };
      }
      return { success: false, fileNotFound: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Delete multiple files
  static async deleteFiles(storagePaths: string[]): Promise<{ success: boolean; errors: string[]; fileNotFoundCount: number }> {
    const deleteResults = await Promise.all(storagePaths.map(path => this.deleteFile(path)));

    const errors: string[] = [];
    let fileNotFoundCount = 0;

    deleteResults.forEach((result, index) => {
      if (result.fileNotFound) {
        fileNotFoundCount++;
      } else if (!result.success) {
        errors.push(`${storagePaths[index]}: ${result.error}`);
      }
    });

    return {
      success: errors.length === 0,
      errors,
      fileNotFoundCount
    };
  }
}
